/**
 * useTranscription — local, private speech-to-text via `/api/transcribe`.
 *
 * Replaces the legacy `useSpeechRecognition` hook, which called Chromium's
 * `webkitSpeechRecognition` — that API silently routes audio to Google and
 * fails outright in packaged Electron (no API key). The new pipeline:
 *
 *   MediaRecorder (renderer) → POST /api/transcribe → Whisper (CUDA) → text
 *
 * All local. Zero network calls outside loopback. Error states are surfaced
 * to the UI (no silent `console.warn` swallowing) so the user knows when
 * the mic is denied, when whisper isn't installed, when Whisper heard no
 * speech (the server answers 422 and its message is shown),
 * or when transcription failed mid-clip.
 *
 * Each upload carries a `MicReport` — the device, its mute state, the settings
 * the browser applied and the peak level a monitor saw — which the server logs,
 * so a recording that comes back wrong shows which microphone made it. A peak
 * of exactly 0 means the browser or OS delivered digital silence (Android does
 * so, without an error, when it withholds the mic), and the message says that
 * instead of "no speech".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { MIC_LEVEL_POLL_MS } from '@/lib/uiConstants';
import type { MicReport, WhisperModel } from '@onestone/shared/schemas';

export type TranscriptionState = 'idle' | 'recording' | 'transcribing' | 'error' | 'unavailable';

interface UseTranscriptionOptions {
  onResult: (transcript: string) => void;
  /** Max seconds per clip — UI hard-stops the recorder. Default 300s. */
  maxSeconds?: number;
  /** Whisper model size — tiny / base / small / medium / large. Default tiny. */
  model?: WhisperModel;
}

interface TranscriptionHook {
  state: TranscriptionState;
  /** Last error message — null when state !== 'error' and !== 'unavailable'. */
  error: string | null;
  isAvailable: boolean;
  /** Start recording. No-op when already recording or unavailable. */
  start: () => Promise<void>;
  /** Stop recording and trigger transcription. */
  stop: () => void;
  /** Toggle convenience for mic buttons. */
  toggle: () => Promise<void>;
}

/** Pick the best MIME type Chromium agrees to record. opus/webm is universal;
 *  the back end's ffmpeg handles any of these formats. */
function pickRecorderMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

/** Shown when the mic delivered only zeros — the device gave this page no sound at all. */
const SILENT_MIC_MESSAGE =
  'The microphone sent no sound at all — every sample was zero, so the device withheld it from this page. On Android: check that Microphone access is on in Quick Settings, and that no other app (a call, a voice assistant, keyboard voice typing) is using the mic.';

/**
 * The level monitor's audio context, created while the mic tap is still the
 * current user gesture: Firefox for Android keeps a context created after an
 * `await` (the permission prompt) suspended, so the monitor never sampled on
 * the phone. Null where the browser has no AudioContext.
 */
function openAudioContext(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  try {
    const ctx = new AudioContext();
    void ctx.resume().catch(() => undefined);
    return ctx;
  } catch {
    return null;
  }
}

/**
 * The highest |sample| the stream delivers while recording — null when this
 * browser can't run the monitor (no context, it failed to build, or it never
 * started), so nothing is concluded from it. The monitor only listens: it
 * never decides whether a recording goes ahead. It owns `ctx` and closes it.
 */
function watchLevel(ctx: AudioContext | null, stream: MediaStream): { peak: () => number | null; stop: () => void } {
  const none = { peak: () => null, stop: () => void ctx?.close().catch(() => undefined) };
  if (!ctx) return none;
  let analyser: AnalyserNode;
  try {
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    ctx.createMediaStreamSource(stream).connect(analyser);
  } catch {
    return none;
  }
  const buf = new Float32Array(analyser.fftSize);
  let peak = 0;
  let sampled = false;
  const timer = window.setInterval(() => {
    if (ctx.state !== 'running') return;
    analyser.getFloatTimeDomainData(buf);
    sampled = true;
    for (const v of buf) peak = Math.max(peak, Math.abs(v));
  }, MIC_LEVEL_POLL_MS);
  return {
    peak: () => (sampled ? peak : null),
    stop: () => {
      window.clearInterval(timer);
      void ctx.close().catch(() => undefined);
    },
  };
}

/** The report for one recording: the track as it stands when the recording stops. */
function micReport(
  track: MediaStreamTrack | undefined,
  muteEvents: number,
  peak: number | null,
  recorderMime: string,
): MicReport {
  const settings: MicReport['settings'] = {};
  for (const [k, v] of Object.entries(track?.getSettings() ?? {})) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') settings[k] = v;
  }
  return {
    label: (track?.label ?? '').slice(0, 200),
    muted: track?.muted ?? false,
    muteEvents,
    readyState: track?.readyState ?? 'none',
    settings,
    peak,
    recorderMime: recorderMime.slice(0, 100),
    userAgent: navigator.userAgent.slice(0, 400),
  };
}

export function useTranscription({
  onResult,
  maxSeconds = 300,
  model = 'tiny',
}: UseTranscriptionOptions): TranscriptionHook {
  const [state, setState] = useState<TranscriptionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const maxTimerRef = useRef<number | null>(null);
  const levelRef = useRef<ReturnType<typeof watchLevel> | null>(null);

  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  // Probe `/api/transcribe/status` once — drives the "unavailable" disabled
  // state on the mic button without waiting for the first failed POST.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/transcribe/status');
        if (cancelled) return;
        const data = (await res.json()) as { available: boolean; reason?: string };
        if (!data.available) {
          setState('unavailable');
          setError(
            data.reason === 'not_installed'
              ? 'Whisper not installed. Run: pip install openai-whisper'
              : 'Local transcription unavailable.',
          );
        }
      } catch {
        // Server unreachable — treat as unavailable; the user sees the same
        // error UI when the chat backend is down anyway.
        if (!cancelled) setState('unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cleanup = useCallback(() => {
    if (maxTimerRef.current !== null) {
      window.clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    levelRef.current?.stop();
    levelRef.current = null;
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const transcribe = useCallback(
    async (blob: Blob, mime: string, mic: MicReport) => {
      setState('transcribing');
      setError(null);
      const form = new FormData();
      const ext = mime.includes('webm') ? 'webm' : mime.includes('ogg') ? 'ogg' : mime.includes('mp4') ? 'm4a' : 'webm';
      form.append('audio', blob, `clip.${ext}`);
      form.append('mic', JSON.stringify(mic));
      try {
        const res = await apiFetch(`/api/transcribe?model=${encodeURIComponent(model)}`, {
          method: 'POST',
          body: form,
        });
        const data = (await res.json().catch(() => ({}))) as { text?: string; error?: string; code?: string };
        if (!res.ok) {
          setState(data.code === 'WHISPER_NOT_INSTALLED' ? 'unavailable' : 'error');
          setError(
            data.code === 'NO_SPEECH' && mic.peak === 0
              ? SILENT_MIC_MESSAGE
              : (data.error ?? `Transcription failed (${res.status})`),
          );
          return;
        }
        const text = (data.text ?? '').trim();
        if (text) onResultRef.current(text);
        setState('idle');
      } catch (e) {
        setState('error');
        setError(e instanceof Error ? e.message : 'Transcription request failed');
      }
    },
    [model],
  );

  const start = useCallback(async () => {
    if (state === 'recording' || state === 'transcribing' || state === 'unavailable') return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setState('unavailable');
      setError('No microphone API in this runtime.');
      return;
    }
    setError(null);
    // Before the first await — see openAudioContext.
    const levelCtx = openAudioContext();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // The plain microphone, as a voice recorder takes it: no call
        // processing. With any of these on, Firefox for Android treats the
        // capture as a call — it switches the phone to communication mode and
        // starts Bluetooth SCO routing (GeckoAppShell.setCommunicationAudioModeOn;
        // MediaEngineWebRTCAudio.h IsVoiceInput: pass-through = "not used for
        // voice input"). Nothing is played back while recording, so there is no
        // echo to cancel, and Whisper handles noise and level itself.
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;
      const track = stream.getAudioTracks()[0];
      let muteEvents = 0;
      track?.addEventListener('mute', () => {
        muteEvents++;
      });
      const level = watchLevel(levelCtx, stream);
      levelRef.current = level;
      const mime = pickRecorderMime();
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = async () => {
        const recordedMime = recorder.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: recordedMime });
        // Read before cleanup stops the track and the monitor.
        const mic = micReport(track, muteEvents, level.peak(), recordedMime);
        cleanup();
        if (blob.size === 0) {
          setState('error');
          setError('No audio captured. Check your microphone.');
          return;
        }
        await transcribe(blob, recordedMime, mic);
      };
      recorder.onerror = (ev) => {
        cleanup();
        setState('error');
        const err = (ev as unknown as { error?: { message?: string } }).error;
        setError(err?.message ?? 'MediaRecorder error');
      };
      recorder.start();
      setState('recording');
      maxTimerRef.current = window.setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
        }
      }, maxSeconds * 1000);
    } catch (e) {
      // The monitor owns the context once it exists; before that (no mic) close it here.
      if (!levelRef.current) void levelCtx?.close().catch(() => undefined);
      cleanup();
      setState('error');
      // DOMException 'NotAllowedError' = user denied; 'NotFoundError' = no device.
      const name = (e as { name?: string }).name;
      if (name === 'NotAllowedError') setError('Microphone permission denied.');
      else if (name === 'NotFoundError') setError('No microphone found.');
      else setError(e instanceof Error ? e.message : 'Could not access microphone.');
    }
  }, [state, maxSeconds, cleanup, transcribe]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
      // onstop fires async — state transition handled there.
    } else {
      cleanup();
      if (state === 'recording') setState('idle');
    }
  }, [state, cleanup]);

  const toggle = useCallback(async () => {
    if (state === 'recording') stop();
    else await start();
  }, [state, start, stop]);

  // Belt-and-suspenders: kill the stream on unmount so a navigation doesn't
  // leave a live mic indicator.
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state === 'recording') recorderRef.current.stop();
      cleanup();
    };
  }, [cleanup]);

  return {
    state,
    error,
    isAvailable: state !== 'unavailable',
    start,
    stop,
    toggle,
  };
}
